import React, { useMemo } from "react";
import { Box } from "@mui/material";
import {
  WifiOff as WifiOffIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { dtGreen } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";

// ===================================================================
//  CONNECTION BANNER — Shows stale/lost connection warning
//
//  - No update for >60s: amber "Last updated X min ago"
//  - No update for >5min: red "Connection lost"
// ===================================================================

const STALE_THRESHOLD_MS = 60 * 1000; // 60s
const LOST_THRESHOLD_MS = 5 * 60 * 1000; // 5 min

interface ConnectionBannerProps {
  lastUpdateTime: string | null;
  isConnected: boolean;
}

const ConnectionBanner: React.FC<ConnectionBannerProps> = ({
  lastUpdateTime,
  isConnected,
}) => {
  const { translate } = useTranslation(["serviceTracking"]);

  const { isStale, isLost, minutesAgo } = useMemo(() => {
    if (!lastUpdateTime) {
      return { isStale: false, isLost: false, minutesAgo: 0 };
    }
    const elapsed = Date.now() - new Date(lastUpdateTime).getTime();
    const mins = Math.floor(elapsed / 60000);

    return {
      isStale: elapsed > STALE_THRESHOLD_MS && elapsed <= LOST_THRESHOLD_MS,
      isLost: elapsed > LOST_THRESHOLD_MS,
      minutesAgo: mins,
    };
  }, [lastUpdateTime]);

  // Force re-render every 15s to keep "X min ago" fresh
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!isStale && !isLost) return;
    const interval = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(interval);
  }, [isStale, isLost]);

  // Don't show anything if connection is fresh
  if (!isStale && !isLost) return null;

  const bgColor = isLost ? "#FEE2E2" : "#FEF3C7"; // red-50 / amber-50
  const borderColor = isLost ? "#FECACA" : "#FDE68A"; // red-200 / amber-200
  const textColor = isLost ? "#991B1B" : "#92400E"; // red-800 / amber-800
  const iconColor = isLost ? "#EF4444" : "#F59E0B"; // red / amber

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 14px",
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: dtGreen.radiusSm,
        animation: "connectionBannerIn 0.3s ease-out both",
      }}
    >
      {isLost ? (
        <WifiOffIcon sx={{ fontSize: 18, color: iconColor, flexShrink: 0 }} />
      ) : (
        <WarningIcon sx={{ fontSize: 18, color: iconColor, flexShrink: 0 }} />
      )}
      <Box
        sx={{
          fontFamily: dtGreen.font,
          fontSize: "12px",
          fontWeight: 500,
          color: textColor,
          lineHeight: 1.3,
        }}
      >
        {isLost
          ? translate("serviceTracking:connectionLost")
          : `${translate("serviceTracking:lastUpdated")} ${minutesAgo} ${translate("serviceTracking:minutesAgo")}`}
      </Box>
    </Box>
  );
};

export default React.memo(ConnectionBanner);
